import type { ReactNode } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ChevronRight,
  Monitor,
  Settings,
  Smartphone,
  Users,
} from 'lucide-react';
import {
  Detail,
  DetailList,
  ErrorState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Skeleton,
  Stat,
  StatRow,
} from '@/ui/Panel';
import { Badge, Code } from '@/ui/Badge';
import { Callout } from '@/ui/Callout';
import { api } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { dateTime, num, relativeTime } from '@/lib/format';
import { readRegisterHealth } from '@/features/system/registers';
import type { RegisterSummary, UserSummary, VersionResponse } from '@/lib/api/types';

/**
 * The System overview.
 *
 * Small on purpose: it answers "is the back end alive, what is running, how
 * many lanes are actually up, how many people can sign in" and then gets out
 * of the way. Everything actionable lives one click down.
 */
export const Route = createFileRoute('/_app/system/')({
  staticData: { crumb: 'Overview' },
  component: SystemOverview,
});

function SystemOverview() {
  const canSeeSync = useAtLeast('manager');

  /**
   * Liveness is `/api/version`, not the API's `/health` — the same reasoning as
   * `app/StatusLine.tsx` and the Today screen, and the same query key so all
   * three agree.
   *
   * `/health` is mounted at the *server root*, and nothing routes the root to
   * the API: the vite dev proxy only forwards `/api`, and in production Caddy
   * sends `/api/*` to the API container and everything else to the front-end.
   * The probe therefore fetched this app's `index.html`, failed to parse it as
   * JSON, and reported a healthy store as "API: Down".
   */
  const version = useQuery({
    queryKey: ['version'],
    queryFn: () => api.get<VersionResponse>('/version'),
    refetchInterval: 30_000,
    staleTime: 25_000,
    retry: 1,
  });
  const health = version;
  /** When the API last actually answered — a client-side fact, not a claim. */
  const lastReply = version.data ? new Date(version.dataUpdatedAt).toISOString() : null;

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => api.get<RegisterSummary[]>('/registers'),
    refetchInterval: 30_000,
  });

  const staff = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/users'),
  });

  const fleet = registers.data ?? [];
  const readings = fleet.map((r) => readRegisterHealth(r));
  const live = readings.filter((r) => r.health === 'live').length;
  const pending = readings.filter((r) => r.health === 'pending').length;
  const degraded = readings.filter(
    (r) => r.health === 'stale' || r.health === 'silent'
  ).length;
  const queueDepth = fleet.reduce((sum, r) => sum + (r.transactionQueueDepth ?? 0), 0);

  const people = staff.data ?? [];
  const activeStaff = people.filter((u) => u.active).length;

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Overview"
        subtitle="Server health, the register fleet and who can sign in."
      />
      <PageBody className="space-y-4">
        {health.isError ? (
          <Callout tone="negative" title="The API is not responding">
            {health.error instanceof Error
              ? health.error.message
              : 'No reply from /api/version.'}{' '}
            Registers cannot sync while this is down.
          </Callout>
        ) : null}

        <StatRow>
          <Stat
            label="API"
            value={health.isLoading ? '…' : health.isError ? 'Down' : 'ok'}
            tone={health.isError ? 'negative' : 'positive'}
            sub={lastReply ? relativeTime(lastReply) : undefined}
          />
          <Stat
            label="Version"
            value={version.data?.version ?? '—'}
            sub={version.data?.buildSha ? version.data.buildSha.slice(0, 7) : undefined}
          />
          <Stat
            label="Registers live"
            value={registers.isLoading ? '…' : `${live}/${fleet.length}`}
            tone={degraded > 0 ? 'warn' : 'positive'}
            sub={
              degraded > 0
                ? `${degraded} not beating`
                : pending > 0
                  ? `${pending} awaiting approval`
                  : 'all lanes healthy'
            }
          />
          <Stat
            label="Queued transactions"
            value={registers.isLoading ? '…' : num(queueDepth)}
            tone={queueDepth > 0 ? 'warn' : undefined}
            sub="unsent across the fleet"
          />
          <Stat
            label="Staff"
            value={staff.isLoading ? '…' : num(activeStaff)}
            sub={`${num(people.length - activeStaff)} deactivated`}
          />
        </StatRow>

        <div className="grid gap-3 lg:grid-cols-2">
          <Panel title="Server">
            {health.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-3 w-1/2" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            ) : health.isError ? (
              <ErrorState
                title="The API is not responding"
                message={health.error instanceof Error ? health.error.message : undefined}
                onRetry={() => void health.refetch()}
              />
            ) : (
              <DetailList columns={2}>
                <Detail label="Version" mono>
                  {version.data?.version ?? '—'}
                </Detail>
                <Detail label="Status">
                  <Badge tone="positive" dot>
                    reachable
                  </Badge>
                </Detail>
                <Detail label="Last reply" mono>
                  {dateTime(lastReply)}
                </Detail>
                <Detail label="Build" mono>
                  {version.data?.buildSha ? (
                    <Code copyable>{version.data.buildSha.slice(0, 12)}</Code>
                  ) : (
                    '—'
                  )}
                </Detail>
                <Detail label="Built" mono>
                  {dateTime(version.data?.buildTime)}
                </Detail>
                <Detail label="Bridge last sync" mono>
                  {version.data?.bridgeLastSync
                    ? relativeTime(version.data.bridgeLastSync)
                    : '—'}
                </Detail>
              </DetailList>
            )}
          </Panel>

          <Panel title="Where to go" bleed>
            <nav className="divide-line-soft divide-y">
              <SectionLink
                to="/system/registers"
                icon={<Monitor />}
                label="Registers"
                description={
                  pending > 0
                    ? `${pending} awaiting approval · ${fleet.length} enrolled`
                    : `${fleet.length} enrolled · ${live} live`
                }
                tone={pending > 0 ? 'warn' : degraded > 0 ? 'warn' : undefined}
              />
              <SectionLink
                to="/system/staff"
                icon={<Users />}
                label="Staff"
                description={`${activeStaff} active of ${people.length}`}
              />
              <SectionLink
                to="/system/floor-devices"
                icon={<Smartphone />}
                label="Floor devices"
                description="Android handhelds: approve, revoke, install QR"
              />
              {canSeeSync ? (
                <SectionLink
                  to="/system/sync"
                  icon={<Activity />}
                  label="Sync health"
                  description="Quarantine, dead letters and queue depth"
                />
              ) : null}
              <SectionLink
                to="/settings"
                icon={<Settings />}
                label="Settings"
                description="Store, tax, tenders, receipt text, WhatsApp, AI"
              />
            </nav>
          </Panel>
        </div>
      </PageBody>
    </Page>
  );
}

function SectionLink({
  to,
  icon,
  label,
  description,
  tone,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  description: string;
  tone?: 'warn';
}) {
  return (
    <Link
      to={to}
      className="hover:bg-surface-hover flex items-center gap-3 px-3 py-2.5 transition-colors"
    >
      <span className="border-line bg-surface-sunken text-ink-muted flex size-7 shrink-0 items-center justify-center rounded border [&_svg]:size-3.5">
        {icon}
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-ink block truncate text-sm">{label}</span>
        <span
          className={
            tone === 'warn'
              ? 'text-2xs text-warn block truncate'
              : 'text-2xs text-ink-faint block truncate'
          }
        >
          {description}
        </span>
      </span>
      <ChevronRight className="text-ink-faint size-3.5 shrink-0" />
    </Link>
  );
}
